import { Event, normalizeEventType } from '../types/event';
import { CardEvent } from '../components/EventCard/EventCard';
import { EventImageType, ShowroomType } from '../types/events';
import i18n from '../i18n';

// Import event card images
import ceuEventImage from '../assets/backgroundImages/eventpageCardImages/CEU.png';
import dealerTrainingEventImage from '../assets/backgroundImages/eventpageCardImages/dealer-training.png';
import publicIntroClassEventImage from '../assets/backgroundImages/eventpageCardImages/public-intro-class.png';
import publicOwnershipExperienceEventImage from '../assets/backgroundImages/eventpageCardImages/public-ownership-experience.png';
import specialEventImage from '../assets/backgroundImages/eventpageCardImages/special-event.png';

const getEventImageType = (event: Event): EventImageType => {
  // Handle case where type might be undefined
  const type = event.type?.toLowerCase() || '';
  
  if (type.includes('ceu')) return 'ceu';
  if (type.includes('dealer') || type.includes('training')) return 'dealer-training';
  if (type.includes('public') && type.includes('introduction')) return 'public-intro-class';
  if (type.includes('ownership')) return 'public-ownership-experience';
  return 'special-event';
};

const getEventImage = (imageType: EventImageType): string => {
  switch (imageType) {
    case 'ceu':
      return ceuEventImage;
    case 'dealer-training':
      return dealerTrainingEventImage;
    case 'public-intro-class':
      return publicIntroClassEventImage;
    case 'public-ownership-experience':
      return publicOwnershipExperienceEventImage;
    default:
      return specialEventImage;
  }
};

// Helper function to properly capitalize showroom names
const normalizeShowroomName = (showroom: string): string => {
  if (!showroom) return '';
  
  // If it's all uppercase, convert to title case
  if (showroom === showroom.toUpperCase()) {
    return showroom
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  // Otherwise return as-is
  return showroom;
};

export const adaptEventToCardEvent = (event: any): CardEvent => {
  // Handle both old and new API response formats
  const id = event.id || event.appointmentId || `event-${Math.random().toString(36).substring(2, 9)}`;
  
  // Get current language
  const currentLanguage = i18n.language || 'en';
  const isFrench = currentLanguage.startsWith('fr');
  
  // Handle case where startDate might be missing
  let formattedDate = '';
  let formattedTime = '';
  
  if (event.startDate) {
    const startDate = new Date(event.startDate);
    // Use language-specific locale for date/time formatting
    const locale = isFrench ? 'fr-CA' : 'en-US';
    
    formattedDate = startDate.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    // Capitalize first letter of weekday and month
    // For French: "mercredi 29 octobre 2025" -> "Mercredi 29 Octobre 2025"
    // For English: "wednesday, october 29, 2025" -> "Wednesday, October 29, 2025"
    formattedDate = formattedDate.replace(/^\w/, (c) => c.toUpperCase()) // Capitalize first letter (weekday)
      .replace(/\s(\w+)\s(\d{4})/, (_match, month, year) => ` ${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`); // Capitalize month
    
    formattedTime = startDate.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } else {
    formattedDate = 'Date TBD';
    formattedTime = 'Time TBD';
  }

  // Safely get event image type even if event.type is undefined
  const imageEventType = getEventImageType(event);

  // Map event subtype to form config key
  let formType = 'special-event'; // Default to special-event for unknown event types
  
  // First check if it's a CEU event (either by type or subtype)
  if (event.type?.toLowerCase() === 'ceu' || event.eventSubtype?.toLowerCase() === 'ceu') {
    formType = 'ceu';
  } else if (event.eventSubtype) {
    switch (event.eventSubtype) {
      case 'Public Ownership Experience':
        formType = 'public-ownership-experience';
        break;
      case 'Dealer Training':
        formType = 'dealer-training';
        break;
      case 'Public Introduction Class':
        formType = 'event-public-intro-class';
        break;
      default:
        // For unknown subtypes, use special-event
        formType = 'special-event';
    }
  } else if (event.name?.toLowerCase().includes('public introduction class')) {
    formType = 'event-public-intro-class';
  }

  // Use language-specific fields if available
  const eventName = isFrench && event.nameFr ? event.nameFr : event.name;
  const eventDescription = isFrench && event.descriptionFr ? event.descriptionFr : event.description;

  return {
    id: id.toString(),
    remoteObjectId: event.remoteObjectId || event.appointmentId || '',
    title: eventName ? eventName.toUpperCase() : 'UNTITLED EVENT',
    // Convert API eventType to our EventType
    type: normalizeEventType(event.eventType),
    duration: event.duration ? `${event.duration}${event.durationUnit ? ' ' + event.durationUnit : 'min'}` : '60min',
    description: eventDescription || '',
    date: formattedDate,
    time: formattedTime,
    location: typeof event.location === 'string' ? event.location : (event.location?.address || event.city || ''),
    city: event.city || (typeof event.location === 'string' ? event.location : event.location?.city) || '',
    showroom: normalizeShowroomName(event.showroom || event.ultimateParent || '') as ShowroomType,
    imageUrl: getEventImage(imageEventType),
    imageEventType,
    formType
  };
};
